import { WEB_EVAL_FPS_OPTIONS } from "./evalVideoEncoding";
import {
  RECORDING_RESOLUTION_OPTIONS,
  RECORDING_RESOLUTION_PRESETS,
} from "./liveTrainingSettings";

/** Web player cameras expose 30 and 60. Resolution matches the replay presets. */
export const COACH_CALIBRATION_FPS = WEB_EVAL_FPS_OPTIONS;
export const COACH_CALIBRATION_RESOLUTIONS = RECORDING_RESOLUTION_OPTIONS;
/** Keep one JPEG under the socket buffer. */
export const CALIBRATION_JPEG_MAX_CHARS = 700000;
export const CALIBRATION_FRAME_MAX_EDGE = 960;

export function normalizeCoachCalibrationSettings(input) {
  const src = input && typeof input === "object" ? input : {};
  const fpsRaw = Number(src.fps);
  const resolutionRaw = String(src.resolution || "")
    .trim()
    .toLowerCase();
  const facingRaw = String(src.facingMode || src.facing_mode || "").trim();
  return {
    fps: COACH_CALIBRATION_FPS.includes(fpsRaw) ? fpsRaw : null,
    resolution: RECORDING_RESOLUTION_OPTIONS.includes(resolutionRaw)
      ? resolutionRaw
      : "",
    deviceId: String(src.deviceId || src.device_id || "").slice(0, 256),
    facingMode:
      facingRaw === "user" || facingRaw === "environment" ? facingRaw : "",
    lens: String(src.lens || "").slice(0, 32),
  };
}

export function calibrationSettingsEqual(a, b) {
  const left = normalizeCoachCalibrationSettings(a);
  const right = normalizeCoachCalibrationSettings(b);
  return (
    left.fps === right.fps &&
    left.resolution === right.resolution &&
    left.deviceId === right.deviceId &&
    left.facingMode === right.facingMode
  );
}

/**
 * The validation still is replaced only after the coach changes FPS,
 * resolution, or lens. Opening the review is a separate first snapshot.
 */
export function shouldRefreshCalibrationFrame(previous, next) {
  if (!previous) return false;
  return !calibrationSettingsEqual(previous, next);
}

/** Keep the current camera fields when the coach patch omits one of them. */
export function mergeCoachCalibrationSettings(current, incoming) {
  const base = normalizeCoachCalibrationSettings(current);
  const next = normalizeCoachCalibrationSettings(incoming);
  return {
    fps: next.fps != null ? next.fps : base.fps,
    resolution: next.resolution || base.resolution,
    deviceId: next.deviceId || base.deviceId,
    facingMode: next.facingMode || base.facingMode,
    lens: next.lens || base.lens,
  };
}

export function resolutionLabelFromFrame(width, height) {
  const short = Math.min(Number(width) || 0, Number(height) || 0);
  if (short >= 1000) return "1080p";
  if (short >= 700) return "720p";
  if (short >= 400) return "480p";
  return "";
}

export function captureSizeForResolution(resolution) {
  const key = String(resolution || "")
    .trim()
    .toLowerCase();
  const preset = RECORDING_RESOLUTION_PRESETS[key];
  if (!preset) return null;
  return { captureWidth: preset.width, captureHeight: preset.height };
}

export function jpegBase64FromDataUrl(
  dataUrl,
  maxChars = CALIBRATION_JPEG_MAX_CHARS
) {
  const raw = String(dataUrl || "");
  const comma = raw.indexOf(",");
  const b64 = (comma >= 0 ? raw.slice(comma + 1) : raw).replace(/\s/g, "");
  if (!b64 || b64.length > maxChars) return null;
  if (!/^[A-Za-z0-9+/=]+$/.test(b64)) return null;
  return b64;
}

export function publicCameraOptions(cameras) {
  return (Array.isArray(cameras) ? cameras : [])
    .filter((camera) => camera && (camera.deviceId || camera.facingMode))
    .slice(0, 8)
    .map((camera) => ({
      id: String(camera.id || camera.deviceId || camera.facingMode).slice(0, 64),
      deviceId: String(camera.deviceId || "").slice(0, 256),
      facingMode:
        camera.facingMode === "user"
          ? "user"
          : camera.facingMode === "environment"
            ? "environment"
            : "",
      lens: String(camera.lens || "").slice(0, 32),
      zoom: Number.isFinite(Number(camera.zoom)) ? Number(camera.zoom) : null,
    }));
}
